import { Component, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { LPGDistData, LpgDistReportSummary, LPGDistService, LpgDistDistanceCost, ToolbarItem, Quota } from '../../lpg-dist.service';
import { DateRange } from '../../../shared/components/date-range.component';
import { TrucksService, Distance } from 'src/app/transportation';
import { LpgDistToolbarComponent } from 'src/app/lpg-dist/lpg-dist-toolbar/lpg-dist-toolbar.component';
import { UserLocalStoreService } from 'src/app/shared/services/user-local-store.service';

import moment from 'moment-jalaali';
import {
  PgDateRangeTitleComponent,
  PgDateRangeTitleEnum, PgTitleComponent,
  PgToolbarComponent
} from '../../../shared/components/title-bar/title-bar.component';

import { PgMessagesService, MessageLevel } from 'src/app/shared/services/pg-messages.service';
import { SharedModule } from 'src/app/shared/shared.module';

interface SummaryReportComponentSettings {
  companyId: string;
  calander: boolean;
}

@Component({
  selector: 'app-summary-report',
  templateUrl: './lpg-dist-costs-report.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule],
})
export class LpgDistCostsReportComponent implements OnInit, PgTitleComponent, PgDateRangeTitleComponent, PgToolbarComponent {
  title = new EventEmitter<string>();
  dateRangeSubTitle = new EventEmitter<[DateRange, PgDateRangeTitleEnum]>();
  toolbarComponentType = LpgDistToolbarComponent;
  toolbarComponent!: LpgDistToolbarComponent;
  message?: string;
  lpgDistData: LPGDistData[] = [];
  organisations: Organisation[] = [];
  sourceSummary: any[] = [];
  destinationSummary: any[] = [];
  dateRange: DateRange = {
    startDate: new Date(),
    endDate: new Date()
  };
  distances: Distance[] = [];
  costSummary: any[] = [];
  quotaValue: Quota[] = [];
  totalQuota?: number;
  totalWeight?: number;
  totalLoading?: number;
  totalremaind?: number;
  totalCost?: number;
  average?: number;
  selectedOrganisation: Organisation = Organisation.create();

  undefinedDistance: boolean = false;

  _settings!: SummaryReportComponentSettings;
  sold?: number;
  sumOfSold?: number;

  constructor(private _lpgDistService: LPGDistService,
    private _organisationService: OrganisationService,
    private _trucksService: TrucksService,
    private _router: Router,
    private _pgMessagesService: PgMessagesService,
    private _userLocalStorage: UserLocalStoreService,
  ) { }

  ngOnInit() {
    this._settings = this._userLocalStorage.get<SummaryReportComponentSettings>('SummaryReportComponent');
    if (!this._settings) {
      this._settings = { companyId: 'iran.parsagroup.orsagas', calander: true };
    }
    const end = moment().endOf('day').toDate();
    this.dateRange = {
      startDate: moment(end).startOf('jMonth').toDate(),
      endDate: end
    };
    this.toolbarComponent.itemsSelected.subscribe(t => {
      this.refresh(t);
    });
    this.fetchData();
  }

  fetchData() {
    forkJoin([
      this._organisationService.get(),
      this._lpgDistService.getQuota(this.dateRange.startDate.getTime(), this.dateRange.endDate.getTime()),
      this._trucksService.getAllDistances()
    ]).subscribe(data => {
      this.organisations = data[0];
      this.distances = data[2];
      this.selectedOrganisation = this.organisations.find(o => o.name === this._settings.companyId) || Organisation.create();
      this.quotaValue = data[1].filter(q => q.organisationId === this.selectedOrganisation.name);
      this.setToolbar();
      this._lpgDistService.getOrganisationSummary(
        this.selectedOrganisation.name,
        this.dateRange.startDate.getTime(),
        this.dateRange.endDate.getTime()).subscribe(lpgDistData => {
        this.lpgDistData = lpgDistData;
        this.sourceSummary = this._lpgDistService.reportSourceSummary2(this.lpgDistData, this.quotaValue);
        this.destinationSummary = this._lpgDistService.reportDestinationSummary(this.lpgDistData, this.quotaValue);
        this.costSummary = this._lpgDistService.sourceDestinationSummary(this.lpgDistData, this.organisations, this.distances, this.quotaValue)
          .sort((t1, t2) => {
            if (t1 && t2 && t1.source > t2.source) {
              return 1;
            } else {
              return -1;
            }
          });
        this.totalWeight = this.getTotalWeight(this.sourceSummary);
        this.totalCost = this.getTotalCost(this.costSummary);
        this.totalQuota = this.getTotalQuota(this.costSummary);
        this.totalLoading = this.getTotalLoading(this.costSummary);
        this.totalremaind = this.getTotalRemaind(this.costSummary);
        this.average = this.getAverageCost(this.costSummary);
        this.sold = this.getSumSold(this.destinationSummary);
        this.sumOfSold = this.getSumOfSolds(this.destinationSummary);
      });
    });
  }

  setToolbar() {
    this.title.emit('Transportation Costs');
    this.dateRangeSubTitle.emit([this.dateRange, PgDateRangeTitleEnum.persian]);
    this.toolbarComponent.visibleOrganisation = true;
    this.toolbarComponent.dateRange = {
      startDate: this.dateRange.startDate,
      endDate: this.dateRange.endDate
    };
    this.toolbarComponent.jalali = this._settings.calander;
    this.toolbarComponent.singleDate = false;
    this.toolbarComponent.organisations = this.organisations
      .filter(o => ['domestic'].filter(s => o.properties[s]).length > 0);
    this.toolbarComponent.selectedOrganisation = this.organisations.find(o => o.name === this.selectedOrganisation.name)
      || Organisation.create();
  }

  getTotalWeight(dataTable: LpgDistReportSummary[]) {
    if (dataTable) {
      return dataTable.reduce(((previousValue, currentValue) =>
        previousValue + currentValue.totalWeight), 0);
    }
  }

  getTotalQuota(dataTable: any) {
    if (dataTable) {
      return dataTable.map((t: any) => t.quota).filter((d: number) => d !== undefined)
        .reduce(((a: number, b: number) => a + b), 0);
    }
  }

  getTotalLoading(dataTable: any) {
    if (dataTable) {
      return dataTable.map((t: any) => t.primaryWeight).filter((d: number) => d !== undefined)
        .reduce(((a: number, b: number) => a + b), 0);
    }
  }

  getTotalRemaind(dataTable: any) {
    if (dataTable) {
      return dataTable.map((t: any) => t.remaind).filter((d: number) => d !== undefined && !Number.isNaN(d))
        .reduce(((a: number, b: number) => a + b), 0);
    }
  }

  getTotalCost(dataTable: LpgDistDistanceCost[]) {
    if (dataTable) {
      return dataTable.map(t => t.cost).filter(d => d !== undefined && !Number.isNaN(d)).reduce(((a, b) => a + b), 0);
    }
  }

  getAverageCost(costs: LpgDistDistanceCost[]) {
    const totalWeight = costs.map(d => d.totalWeight).filter(c => c !== undefined).reduce(((a, b) => a + b), 0);
    const totalCost = costs.map(d => d.cost).filter(d => !Number.isNaN(d)).reduce(((a, b) => a + b), 0);
    return Math.round(totalCost / totalWeight);
  }

  getSumSold(dataTable: any) {
    if (dataTable) {
      return dataTable.map((t: any) => t.lastSold).filter((d: number) => d !== undefined && !Number.isNaN(d))
        .reduce(((a: number, b: number) => a + b), 0);
    }
  }

  getSumOfSolds(dataTable: any) {
    if (dataTable) {
      return dataTable.map((t: any) => t.sumOfSolds).filter((d: number) => d !== undefined && !Number.isNaN(d))
        .reduce(((a: number, b: number) => a + b), 0);
    }

  }

  organisationNameFormatter(organisationId: string) {
    const o = this.organisations.find(org => org.name === organisationId);
    return (o) ? o.nameTranslations['fa'] : '';
  }

  editRecord(item: any) {
    this._router.navigate(['lpg-dist/quta-entry'], {
      queryParams: {
        company: this.selectedOrganisation.name,
        sourceOrganisationId: item.source,
        destination: item.destination,
      }
    });
  }

  delete(item: any) {
    if (confirm('Are you sure you want to delete this Quota?')) {
      const quotas = this.quotaValue.filter(d =>
        d.destination === item.destination &&
        d.organisationId === this.selectedOrganisation.name &&
        d.sourceOrganisationId === item.source &&
        d.quotaType === 'destination'
      );
      if (quotas) {
        const quota = quotas.sort((a, b) => b.timestamp - a.timestamp)[0];
        quota.isDeleted = true;
        this._lpgDistService.updateQuota(quota).subscribe(() => {
          this.fetchData();
          this.message = 'Remove Quota';
          this._pgMessagesService.publishMessage({
            message: this.message,
            topic: 'Deleted',
            level: MessageLevel.Info,
          });
        });
      }
    }
  }

  refresh(item: ToolbarItem) {
    this._settings = {
      companyId: item.companyId || '',
      calander: item.selectedCalander || false
    };
    this.dateRange = item.dateRange;
    this._userLocalStorage.save('SummaryReportComponent', this._settings);
    this.fetchData();
  }
}
